import { graphql } from '@api/generated';
import { AnnounceRecording, RecordingAction } from '@api/generated/graphql';
import { useMutation } from '@apollo/client/react';
import ToastModule from '@modules/ToastModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import {
  RecordingState,
  RecordingStatus,
} from '@modules/SoftPhoneModule/types';
import { RecordingTooltip } from './RecordingTooltip';
import { defaultRecordingState } from '@modules/SoftPhoneModule/lib/getRecordingState';

/** Returns the tooltip for a given recording status. */
const getRecordingTooltip = (recordingState: RecordingState): string => {
  if (recordingState.status === RecordingStatus.Unknown) {
    return RecordingTooltip.UNKNOWN;
  }

  if (
    recordingState.status === RecordingStatus.NoRecording &&
    recordingState.disabled
  ) {
    return RecordingTooltip.RECORDING_NOT_ENABLED;
  }

  if (recordingState.announceRecording === AnnounceRecording.PromptToAnnounce) {
    return RecordingTooltip.RECORDING_ENABLED;
  }

  return '';
};

export const UPDATE_RECORDING_ACTION_MUTATION = graphql(`
  mutation updateRecordingAction(
    $kazooCallId: String!
    $action: RecordingAction!
  ) {
    updateRecordingAction(kazooCallId: $kazooCallId, action: $action) {
      status
    }
  }
`);

const showRecordingError = () => {
  ToastModule.showToast({
    status: 'critical',
    title: 'Failed to perform recording action',
    toastBody: 'Please try again',
  });
};

const useRecording = (callId: string) => {
  const { callAttributes } = usePhoneState();
  const recordingState = callAttributes[callId]
    ? callAttributes[callId].recordingState
    : defaultRecordingState;
  const [mutate, { loading }] = useMutation(UPDATE_RECORDING_ACTION_MUTATION);

  const toggleRecording = async (kazooCallId: string) => {
    let action: RecordingAction;

    switch (recordingState.status) {
      case RecordingStatus.Paused:
        action = RecordingAction.Resume;
        break;
      case RecordingStatus.Started:
      case RecordingStatus.Muted:
        action = RecordingAction.Pause;
        break;
      default: // covers RecordingStatus.NoRecording, RecordingStatus.Stopped
        action = RecordingAction.Start;
    }

    try {
      const result = await mutate({
        variables: {
          kazooCallId,
          action,
        },
      });

      if (result.data?.updateRecordingAction.status) {
        SoftPhoneModule.setRecordingStatus(
          callId,
          result.data?.updateRecordingAction.status,
        );
      } else {
        showRecordingError();
      }
    } catch (_error) {
      showRecordingError();
    }
  };

  return {
    toggleRecording,
    isRecording:
      recordingState.status === RecordingStatus.Started ||
      recordingState.status === RecordingStatus.Muted,
    isRecordingLoading: loading,
    isRecordingDisabled: recordingState.disabled,
    recordingTooltip: getRecordingTooltip(recordingState),
  };
};

export default useRecording;
